import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const api = axios.create({ baseURL: API_URL });

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    // Solo cerrar sesion si el token es el problema; una contraseña mal escrita
    // (login, cajon, confirmaciones) tambien responde 401 y no debe sacar al usuario.
    const sesionInvalida = ['Token requerido', 'Token inválido', 'No autenticado']
      .includes(err.response?.data?.error);
    if (err.response?.status === 401 && sesionInvalida) {
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

export const authService = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  solicitarRecuperacion: (email: string) => api.post('/auth/solicitar-recuperacion', { email }),
  getSolicitudesRecuperacion: () => api.get('/auth/solicitudes-recuperacion'),
  resolverSolicitud: (id: string) => api.post(`/auth/solicitudes-recuperacion/${id}/resolver`),
};

export const pointService = {
  config: () => api.get('/point/config'),
  ajustes: () => api.get('/point/ajustes'),
  probar: (accessToken?: string) => api.post('/point/probar', { accessToken }),
  guardarAjustes: (data: { accessToken?: string; terminalId: string; enabled: boolean }) => api.put('/point/ajustes', data),
  pendiente: (comanda_id: string, subcuenta_id?: string | null) => api.get('/point/pendiente', { params: { comanda_id, subcuenta_id } }),
  crear: (data: { comanda_id: string; subcuenta_id?: string | null; propina: number }) => api.post('/point/cobros', data),
  consultar: (id: string) => api.post(`/point/cobros/${encodeURIComponent(id)}/consultar`),
};

export const mesasService = {
  getAll: () => api.get('/mesas'),
  crear: (numero: number, capacidad: number, zona?: string) => api.post('/mesas', { numero, capacidad, zona }),
  eliminar: (id: string) => api.delete(`/mesas/${id}`),
  updateEstado: (id: string, estado: string, personas_actual?: number) =>
    api.patch(`/mesas/${id}/estado`, { estado, personas_actual }),
  updatePersonas: (id: string, personas_actual: number) =>
    api.patch(`/mesas/${id}/personas`, { personas_actual }),
  actualizar: (id: string, data: { numero?: number; capacidad?: number; zona?: string }) =>
    api.patch(`/mesas/${id}`, data),
  unirMesas: (id: string, mesa_unir_id: string | null) =>
    api.patch(`/mesas/${id}/unir`, { mesa_unir_id }),
};

export const objetivosService = {
  getAll:       ()                      => api.get('/objetivos'),
  sincronizar:  ()                      => api.post('/objetivos/sincronizar'),
  crear:        (data: any)             => api.post('/objetivos', data),
  actualizar:   (id: string, data: any) => api.patch(`/objetivos/${id}`, data),
  eliminar:     (id: string)            => api.delete(`/objetivos/${id}`),
};

export const promocionesService = {
  getAll:    () => api.get('/promociones'),
  crear:     (data: any) => api.post('/promociones', data),
  actualizar:(id: string, data: any) => api.patch(`/promociones/${id}`, data),
  eliminar:  (id: string) => api.delete(`/promociones/${id}`),
};

export const proveedoresService = {
  getAll:     () => api.get('/proveedores'),
  crear:      (data: any) => api.post('/proveedores', data),
  actualizar: (id: string, data: any) => api.patch(`/proveedores/${id}`, data),
  eliminar:   (id: string) => api.delete(`/proveedores/${id}`),
};

export const cuponesService = {
  getAll:     () => api.get('/cupones'),
  crear:      (data: any) => api.post('/cupones', data),
  actualizar: (id: string, data: any) => api.patch(`/cupones/${id}`, data),
  eliminar:   (id: string) => api.delete(`/cupones/${id}`),
};

export const productosService = {
  getAll: () => api.get('/productos'),
  crear: (data: any) => api.post('/productos', data),
  actualizar: (id: string, data: any) => api.put(`/productos/${id}`, data),
  eliminar: (id: string) => api.delete(`/productos/${id}`),
};

export const categoriasService = {
  getAll: () => api.get('/categorias'),
  crear: (data: any) => api.post('/categorias', data),
  actualizar: (id: string, data: any) => api.put(`/categorias/${id}`, data),
  eliminar: (id: string) => api.delete(`/categorias/${id}`),
};

export const inventarioService = {
  // Insumos
  getInsumos:  () => api.get('/inventario/insumos'),
  crearInsumo: (data: any) => api.post('/inventario/insumos', data),
  actualizarInsumo: (id: string, data: any) => api.put(`/inventario/insumos/${id}`, data),
  eliminarInsumo: (id: string) => api.delete(`/inventario/insumos/${id}`),
  getAlertas:  () => api.get('/inventario/alertas'),
  // Entradas y salidas: el stock no se escribe a mano, se mueve con motivo
  registrarMovimiento: (id: string, data: { tipo: 'entrada' | 'salida'; cantidad: number; motivo: string }) =>
    api.post(`/inventario/insumos/${id}/movimiento`, data),
  getMovimientos: (id: string) => api.get(`/inventario/insumos/${id}/movimientos`),
  getMovimientosTodos: (tipo?: string) => api.get('/inventario/movimientos', { params: tipo ? { tipo } : {} }),
  // Apartados (pre-descuento, migración 021)
  getReservas: () => api.get('/inventario/reservas'),
  recalcularReservas: () => api.post('/inventario/reservas/recalcular'),
  // Recetas
  getReceta:   (producto_id: string) => api.get(`/inventario/recetas/${producto_id}`),
  agregarIngrediente: (data: any) => api.post('/inventario/recetas', data),
  eliminarIngrediente: (id: string) => api.delete(`/inventario/recetas/${id}`),
  getHistorialReceta: (producto_id: string) => api.get(`/inventario/recetas/${producto_id}/historial`),
  // Costos HU13
  getCostos:   () => api.get('/inventario/costos'),
  // Mermas HU14/HU15
  getMermas:   (fi?: string, ff?: string) =>
    api.get(`/inventario/mermas${fi && ff ? `?fecha_inicio=${fi}&fecha_fin=${ff}` : ''}`),
  crearMerma:  (data: any) => api.post('/inventario/mermas', data),
  getResumenMermas: (fi: string, ff: string) =>
    api.get(`/inventario/mermas/resumen?fecha_inicio=${fi}&fecha_fin=${ff}`),
  // Pruebas de rendimiento (migración 026)
  getPruebasRendimiento: (fi?: string, ff?: string) =>
    api.get(`/inventario/pruebas-rendimiento${fi && ff ? `?fecha_inicio=${fi}&fecha_fin=${ff}` : ''}`),
};

export const comidaEmpleadosService = {
  getEmpleados: () => api.get('/comida-empleados/empleados'),
  getAll: (params?: { fecha_inicio?: string; fecha_fin?: string; empleado_id?: string; limite?: number }) => {
    const q = new URLSearchParams(params as any).toString();
    return api.get(`/comida-empleados${q ? `?${q}` : ''}`);
  },
  registrar:  (data: any)   => api.post('/comida-empleados', data),
  cancelar:   (id: string)  => api.post(`/comida-empleados/${id}/cancelar`),
  getPendientes: () => api.get('/comida-empleados/pendientes'),
  aprobar:  (id: string, password: string, extra = false) => api.post(`/comida-empleados/${id}/aprobar`, { password, extra }),
  rechazar: (id: string, password: string) => api.post(`/comida-empleados/${id}/rechazar`, { password }),
  getResumen: (fi: string, ff: string) =>
    api.get(`/comida-empleados/resumen?fecha_inicio=${fi}&fecha_fin=${ff}`),
};

export const eventosService = {
  getAll: (fi?: string, ff?: string) =>
    api.get(`/eventos${fi && ff ? `?fecha_inicio=${fi}&fecha_fin=${ff}` : ''}`),
  getProximos: () => api.get('/eventos/proximos'),
  crear: (data: any) => api.post('/eventos', data),
  actualizar: (id: string, data: any) => api.put(`/eventos/${id}`, data),
  eliminar: (id: string) => api.delete(`/eventos/${id}`),
};

export const facturasService = {
  crear:        (data: any) => api.post('/facturas', data),
  getPorPago:   (pago_id: string) => api.get(`/facturas/${pago_id}`),
  descargarPdf: (id: string) => api.get(`/facturas/${id}/pdf`, { responseType: 'blob' }),
  descargarXml: (id: string) => api.get(`/facturas/${id}/xml`, { responseType: 'blob' }),
  cancelar:     (id: string) => api.post(`/facturas/${id}/cancelar`),
};

export const reservacionesService = {
  getAll:     (fecha?: string)        => api.get(`/reservaciones${fecha ? `?fecha=${fecha}` : ''}`),
  crear:      (data: any)             => api.post('/reservaciones', data),
  actualizar: (id: string, data: any) => api.put(`/reservaciones/${id}`, data),
  cancelar:   (id: string)            => api.post(`/reservaciones/${id}/cancelar`),
  cerrar:     (id: string, estado: 'completada' | 'no_asistio') => api.post(`/reservaciones/${id}/estado`, { estado }),
};

export const auditoriaService = {
  get: (params?: { fecha_inicio?: string; fecha_fin?: string; modulo?: string; limite?: number }) => {
    const q = new URLSearchParams(params as any).toString();
    return api.get(`/auditoria${q ? `?${q}` : ''}`);
  },
  getResumen: (fi?: string, ff?: string) =>
    api.get(`/auditoria/resumen${fi && ff ? `?fecha_inicio=${fi}&fecha_fin=${ff}` : ''}`),
};

export const configuracionService = {
  get: () => api.get('/configuracion'),
  put: (data: any) => api.put('/configuracion', data),
  abrirCajon: (ip?: string, puerto?: number) =>
    api.post('/configuracion/abrir-cajon', { ip, puerto }),
};

export const usuariosService = {
  getAll: () => api.get('/usuarios'),
  crear: (data: any) => api.post('/usuarios', data),
  actualizar: (id: string, data: any) => api.patch(`/usuarios/${id}`, data),
  // Solo funciona con usuarios sin historial; el backend explica por que no.
  eliminar: (id: string, password_admin: string) => api.delete(`/usuarios/${id}`, { data: { password_admin } }),
};

export const metodosPagoService = {
  getAll:     () => api.get('/metodos-pago'),
  crear:      (data: { clave: string; nombre: string; color?: string }) => api.post('/metodos-pago', data),
  // clave_sat_forma_pago: clave del catálogo SAT ('01' efectivo, '04' tarjeta…)
  // que usa el timbrado. El PATCH del backend ya la acepta.
  actualizar: (clave: string, data: { nombre?: string; color?: string; activo?: boolean; clave_sat_forma_pago?: string }) =>
    api.patch(`/metodos-pago/${clave}`, data),
  eliminar:   (clave: string) => api.delete(`/metodos-pago/${clave}`),
};

export const cajaService = {
  // subcuenta_id: 'base' = cuenta base de la mesa (Mesa 3), uuid = sub-mesa (Mesa 3.1)
  getCuenta: (mesa_id: string | number, subcuenta_id?: string | null) =>
    api.get(`/caja/cuenta/${mesa_id}${subcuenta_id ? `?subcuenta_id=${subcuenta_id}` : ''}`),
  getMesasCuentas: () => api.get('/caja/mesas-cuentas'),
  getCorte: (fecha: string) => api.get(`/caja/corte?fecha=${fecha}`),
  cerrarCorte: (fecha: string) => api.post('/caja/corte/cerrar', { fecha }),
  getTopProductos: (fi: string, ff: string, limite = 10) =>
    api.get(`/caja/top-productos?fecha_inicio=${fi}&fecha_fin=${ff}&limite=${limite}`),
  getTiempos: (fi: string, ff: string) =>
    api.get(`/caja/tiempos?fecha_inicio=${fi}&fecha_fin=${ff}`),
  getReporteCompleto: (fi: string, ff: string) =>
    api.get(`/caja/reporte-completo?fecha_inicio=${fi}&fecha_fin=${ff}`),
  getVentasPorDia: (fi: string, ff: string) =>
    api.get(`/caja/ventas-por-dia?fecha_inicio=${fi}&fecha_fin=${ff}`),
  getReporteClientes: (fi: string, ff: string) =>
    api.get(`/caja/reporte-clientes?fecha_inicio=${fi}&fecha_fin=${ff}`),
  registrarPago: (data: any) => api.post('/caja/pago', data),
  getParaLlevar: () => api.get('/caja/para-llevar'),
  getCuentaPorComanda: (comanda_id: string) => api.get(`/caja/subcuenta/${comanda_id}`),
  cancelarCuenta: (comanda_id: string, motivo?: string) => api.post(`/caja/cancelar/${comanda_id}`, { motivo }),
  getHistorial: (params: Record<string, any>) => api.get('/caja/historial', { params }),
  getTicketDetalle: (pago_id: string) => api.get(`/caja/historial/${pago_id}`),
  // Manda el ticket a la impresora térmica de caja: sale solo, sin diálogo
  reimprimirTicket: (pago_id: string) => api.post(`/caja/reimprimir/${pago_id}`),
  reabrirCuenta: (pago_id: string) => api.post(`/caja/reabrir/${pago_id}`),
  // Anula un ticket cobrado (reabrir + cancelar en una transacción).
  cancelarTicket: (pago_id: string, motivo?: string) => api.post(`/caja/historial/${pago_id}/cancelar`, { motivo }),
  // Corregir con qué se cobró un ticket, sin reabrir y volver a cobrar la cuenta
  cambiarComensales: (pago_id: string, comensales: number) =>
    api.patch(`/caja/historial/${pago_id}/comensales`, { comensales }),
  cambiarMetodoPago: (pago_id: string, datos: {
    metodo: string; metodo_2?: string | null;
    monto_metodo_1?: number | null; monto_metodo_2?: number | null;
    propina_metodo?: string | null;
  }) => api.patch(`/caja/historial/${pago_id}/metodo`, datos),
  // subcuenta_id: 'base' (Mesa 3) o el id de la sub-mesa (Mesa 3.1); null = mesa sin dividir
  // descuento_items: descuentos por producto capturados en caja, para que la
  // pre-cuenta impresa muestre lo mismo que la pantalla
  imprimirPrecuenta: (
    comanda_id: string,
    subcuenta_id: string | null = null,
    descuento_items: { item_id: string; descuento_monto: number }[] = [],
    descuento_cuenta: { descuento_tipo: 'porcentaje' | 'monto'; descuento_valor: number } | null = null,
  ) => api.post('/caja/imprimir-precuenta', { comanda_id, subcuenta_id, descuento_items, ...descuento_cuenta }),
  // Pasa todo lo de `origen` a `destino` (cada uno { mesa_id } o { comanda_id })
  unirCuentas: (origen: { mesa_id?: string; comanda_id?: string }, destino: { mesa_id?: string; comanda_id?: string }) =>
    api.post('/caja/unir', { origen, destino }),
};

export const comandasService = {
  getActivas: () => api.get('/comandas'),
  updateEstado: (id: string, estado: string, area?: 'cocina' | 'barra') =>
    api.patch(`/comandas/${id}/estado`, area ? { estado, area } : { estado }),
  updateItems: (id: string, data: { agregar?: any[]; eliminar?: string[]; decrementar?: string[]; password?: string }) =>
    api.patch(`/comandas/${id}/items`, data),
  // Feature 1 — Tiempos
  enviarTiempo: (id: string, tiempo: number) =>
    api.post(`/comandas/${id}/enviar-tiempo`, { tiempo }),
  // Feature 2 — Notas por ítem
  updateNotasItem: (id: string, item_id: string, notas: string) =>
    api.patch(`/comandas/${id}/items/${item_id}/notas`, { notas }),
  // Feature 3 — Subcuentas
  getSubcuentas: (id: string) => api.get(`/comandas/${id}/subcuentas`),
  // Divide la mesa en `partes` cuentas: la base (Mesa 3) + partes-1 sub-mesas
  crearSubcuentas: (id: string, partes: number) =>
    api.post(`/comandas/${id}/subcuentas`, { partes }),
  borrarSubcuentas: (id: string) => api.delete(`/comandas/${id}/subcuentas`),
  moverItemSubcuenta: (id: string, item_id: string, subcuenta_id: string | null) =>
    api.patch(`/comandas/${id}/items/${item_id}/subcuenta`, { subcuenta_id }),
  dividirItem: (id: string, item_id: string, partes: number) =>
    api.post(`/comandas/${id}/items/${item_id}/dividir`, { partes }),
  // Feature 4 — Cargos extra
  addCargo: (id: string, concepto: string, monto: number, subcuenta_id?: string) =>
    api.post(`/comandas/${id}/cargos`, { concepto, monto, subcuenta_id }),
  deleteCargo: (id: string, cargo_id: string) =>
    api.delete(`/comandas/${id}/cargos/${cargo_id}`),
};

export const modificadoresService = {
  getAll:            ()                          => api.get('/modificadores'),
  getDeProducto:     (producto_id: string)       => api.get(`/modificadores/producto/${producto_id}`),
  crearGrupo:        (data: any)                 => api.post('/modificadores', data),
  actualizarGrupo:   (id: string, data: any)     => api.patch(`/modificadores/${id}`, data),
  eliminarGrupo:     (id: string)                => api.delete(`/modificadores/${id}`),
  crearOpcion:       (grupo_id: string, data: any) => api.post(`/modificadores/${grupo_id}/opciones`, data),
  actualizarOpcion:  (opcion_id: string, data: any) => api.patch(`/modificadores/opciones/${opcion_id}`, data),
  eliminarOpcion:    (opcion_id: string)         => api.delete(`/modificadores/opciones/${opcion_id}`),
  setGruposProducto: (producto_id: string, grupos: any[]) => api.put(`/modificadores/producto/${producto_id}`, { grupos }),
};

export const requisicionesService = {
  getAll:    (params?: { tipo?: string; estado?: string; limite?: number }) => {
    const q = new URLSearchParams(params as any).toString();
    return api.get(`/requisiciones${q ? `?${q}` : ''}`);
  },
  getById:   (id: string)   => api.get(`/requisiciones/${id}`),
  crear:     (data: any)    => api.post('/requisiciones', data),
  actualizar:(id: string, data: any) => api.patch(`/requisiciones/${id}`, data),
  aplicar:   (id: string)   => api.post(`/requisiciones/${id}/aplicar`),
  eliminar:  (id: string)   => api.delete(`/requisiciones/${id}`),
};

export const turnosService = {
  getActivo:           ()                              => api.get('/turnos/activo'),
  getAll:              (limite = 20, offset = 0)       => api.get(`/turnos?limite=${limite}&offset=${offset}`),
  getById:             (id: string)                    => api.get(`/turnos/${id}`),
  abrir:               (data: { fondo_inicial: number; notas_apertura?: string }) =>
                         api.post('/turnos/abrir', data),
  cerrar:              (id: string, data: { fondo_final: number; notas_cierre?: string; declaracion_cierre?: Record<string, number> }) =>
                         api.post(`/turnos/${id}/cerrar`, data),
  // mesero_id solo va cuando el retiro paga las propinas acumuladas de un mesero.
  registrarMovimiento: (id: string, data: { tipo: 'retiro' | 'deposito'; monto: number; motivo: string; mesero_id?: string; categoria?: string }) =>
                         api.post(`/turnos/${id}/movimiento`, data),
  cancelarMovimiento:  (turnoId: string, movId: string) =>
                         api.delete(`/turnos/${turnoId}/movimientos/${movId}`),
  imprimirCorte:       (turnoId: string, data: any) =>
                         api.post(`/turnos/${turnoId}/imprimir-corte`, data),
  imprimirPropinas:    (data: { propinas: Record<string, number>; cajero?: string }) =>
                         api.post('/turnos/imprimir-propinas', data),
};

export default api;
